import { useState, useEffect } from 'react'
import { apiFetch } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useMoneda } from '../context/MonedaContext'
import TicketImprimible from '../components/TicketImprimible'

const METODO_LABEL = {
  'Efectivo': '💵 Efectivo',
  'Tarjeta de Débito': '💳 Tarjeta de Débito',
  'Tarjeta de Crédito': '💳 Tarjeta de Crédito',
  'Transferencia': '🏦 Transferencia',
  efectivo: '💵 Efectivo', tarjeta: '💳 Tarjeta', transferencia: '🏦 Transferencia',
}

function exportarExcel(ventas, detallesPorVenta) {
  const filas = [['Folio','Fecha','Vendedor','Estado','Método Pago','Producto','Código','Cantidad','Precio Unit.','Subtotal','Total Venta']]
  ventas.forEach(v => {
    const detalles = detallesPorVenta[v.id] || []
    if (detalles.length === 0) {
      filas.push([`#${v.folio}`, new Date(v.fecha).toLocaleString('es-CL'), v.perfiles?.nombre||'-', v.estado||'completada', v.metodo_pago||'efectivo','','','','','',v.total])
    } else {
      detalles.forEach((d,i) => {
        filas.push([
          i===0?`#${v.folio}`:'', i===0?new Date(v.fecha).toLocaleString('es-CL'):'',
          i===0?(v.perfiles?.nombre||'-'):'', i===0?(v.estado||'completada'):'',
          i===0?(v.metodo_pago||'efectivo'):'',
          d.productos?.nombre||'-', d.productos?.codigo||'',
          d.cantidad, d.precio_unitario, d.subtotal, i===0?v.total:''
        ])
      })
    }
  })
  const csv = filas.map(f => f.map(c => { const s=String(c??''); return s.includes(',')||s.includes('"')?`"${s.replace(/"/g,'""')}"`:s }).join(',')).join('\n')
  const blob = new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8;'})
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a'); a.href=url; a.download=`historial-ventas-${new Date().toISOString().split('T')[0]}.csv`; a.click()
  URL.revokeObjectURL(url)
}

export default function Historial() {
  const { perfil } = useAuth()
  const { formatearMoneda } = useMoneda()
  const [ventas, setVentas] = useState([])
  const [ventaSeleccionada, setVentaSeleccionada] = useState(null)
  const [detalles, setDetalles] = useState([])
  const [loading, setLoading] = useState(true)
  const [exportando, setExportando] = useState(false)
  const [ticketVenta, setTicketVenta] = useState(null)
  const [cancelando, setCancelando] = useState(null)
  const [filtroFolio, setFiltroFolio] = useState('')
  const [filtroVendedor, setFiltroVendedor] = useState('')
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('')
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('')
  const [pestaña, setPestaña] = useState('ventas')
  const [facturas, setFacturas] = useState([])
  const [loadingCompras, setLoadingCompras] = useState(false)

  useEffect(() => { cargarVentas() }, [])

  const cargarVentas = async () => {
    setLoading(true)
    try {
      const data = await apiFetch('/ventas')
      if (Array.isArray(data)) {
        setVentas(data)
      } else {
        setVentas([])
      }
    } catch (err) {
      console.warn('No se pudieron obtener las ventas del servidor:', err)
      setVentas([])
    } finally {
      setLoading(false)
    }
  }

  const cargarFacturas = async () => {
    setLoadingCompras(true)
    try {
      const data = await apiFetch('/compras/facturas')
      setFacturas(Array.isArray(data) ? data : data?.facturas || [])
    } catch (e) {
      console.warn('Error cargando facturas:', e)
      setFacturas([])
    } finally {
      setLoadingCompras(false)
    }
  }

  const cargarDetalles = async (ventaId) => {
    try {
      const data = await apiFetch(`/ventas/${ventaId}/detalle`)
      if (Array.isArray(data)) {
        setDetalles(data)
      } else {
        setDetalles([])
      }
      setVentaSeleccionada(ventaId)
    } catch {
      setDetalles([])
      setVentaSeleccionada(ventaId)
    }
  }

  const cancelarVenta = async (venta) => {
    if (!confirm(`¿Cancelar venta #${venta.folio}? Se repondrá el stock de los productos.`)) return
    setCancelando(venta.id)
    try {
      await apiFetch(`/ventas/${venta.id}/cancelar`, { method: 'POST' })
    } catch (err) {
      console.warn('Cancelación local:', err)
    } finally {
      setVentas(prev => prev.map(v => v.id === venta.id ? { ...v, estado: 'cancelada' } : v))
      setCancelando(null)
    }
  }

  const abrirTicket = async (venta) => {
    try {
      const data = await apiFetch(`/ventas/${venta.id}/detalle`)
      const items = Array.isArray(data) ? data : []
      setTicketVenta({ venta, detalles: items })
    } catch {
      setTicketVenta({ venta, detalles: [] })
    }
  }

  const manejarExportar = async () => {
    setExportando(true)
    try {
      const detallesMap = {}
      for (const v of ventasFiltro) {
        try {
          const data = await apiFetch(`/ventas/${v.id}/detalle`)
          detallesMap[v.id] = Array.isArray(data) ? data : []
        } catch {
          detallesMap[v.id] = []
        }
      }
      exportarExcel(ventasFiltro, detallesMap)
    } catch (e) { alert('Error al exportar: ' + e.message) } finally { setExportando(false) }
  }

  const formatearFecha = (f) => new Date(f).toLocaleString('es-CL', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })

  const ventasFiltro = ventas.filter(v => {
    if (filtroFolio && !String(v.folio).includes(filtroFolio)) return false
    if (filtroVendedor && !(v.perfiles?.nombre||'').toLowerCase().includes(filtroVendedor.toLowerCase())) return false
    if (filtroFechaDesde && new Date(v.fecha) < new Date(filtroFechaDesde+'T00:00:00')) return false
    if (filtroFechaHasta && new Date(v.fecha) > new Date(filtroFechaHasta+'T23:59:59')) return false
    return true
  })

  const totalFiltrado = ventasFiltro.filter(v => v.estado !== 'cancelada').reduce((s,v) => s+Number(v.total), 0)
  const ventaDetalle = ventas.find(v => v.id === ventaSeleccionada)

  if (loading) return <div className="main-content"><div className="page-header"><h1 className="page-title">Historial de Ventas</h1></div><div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Cargando ventas...</div></div>

  return (
    <div className="main-content">
      <div className="page-header">
        <h1 className="page-title">Historial</h1>
        <p className="page-subtitle">{pestaña === 'ventas' ? `${ventas.length} ventas registradas` : `${facturas.length} facturas registradas`}</p>
      </div>

      <div style={{ display: 'flex', gap: '1rem', borderBottom: '2px solid var(--border)', marginBottom: '1.5rem' }}>
        <button onClick={() => setPestaña('ventas')}
          style={{ padding: '0.75rem 1rem', borderBottom: pestaña === 'ventas' ? '3px solid var(--primary)' : 'none',
            color: pestaña === 'ventas' ? 'var(--primary)' : 'var(--text-light)',
            background: 'none', border: 'none', cursor: 'pointer', fontWeight: pestaña === 'ventas' ? '600' : '400' }}>
          📊 Ventas
        </button>
        <button onClick={() => { setPestaña('compras'); cargarFacturas() }}
          style={{ padding: '0.75rem 1rem', borderBottom: pestaña === 'compras' ? '3px solid var(--primary)' : 'none',
            color: pestaña === 'compras' ? 'var(--primary)' : 'var(--text-light)',
            background: 'none', border: 'none', cursor: 'pointer', fontWeight: pestaña === 'compras' ? '600' : '400' }}>
          📦 Compras / Facturas
        </button>
      </div>

      {pestaña === 'ventas' && (
      <>
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', alignItems: 'end' }}>
          <div>
            <label style={{ display:'block', fontSize:'0.875rem', fontWeight:'600', marginBottom:'0.4rem', color:'var(--text-light, #64748b)' }}>Folio</label>
            <input className="form-input" type="text" placeholder="Ej: 1001" value={filtroFolio} onChange={e => setFiltroFolio(e.target.value)} />
          </div>
          <div>
            <label style={{ display:'block', fontSize:'0.875rem', fontWeight:'600', marginBottom:'0.4rem', color:'var(--text-light, #64748b)' }}>Vendedor</label>
            <input className="form-input" type="text" placeholder="Nombre..." value={filtroVendedor} onChange={e => setFiltroVendedor(e.target.value)} />
          </div>
          <div>
            <label style={{ display:'block', fontSize:'0.875rem', fontWeight:'600', marginBottom:'0.4rem', color:'var(--text-light, #64748b)' }}>Desde</label>
            <input className="form-input" type="date" value={filtroFechaDesde} onChange={e => setFiltroFechaDesde(e.target.value)} />
          </div>
          <div>
            <label style={{ display:'block', fontSize:'0.875rem', fontWeight:'600', marginBottom:'0.4rem', color:'var(--text-light, #64748b)' }}>Hasta</label>
            <input className="form-input" type="date" value={filtroFechaHasta} onChange={e => setFiltroFechaHasta(e.target.value)} />
          </div>
          <div>
            <button className="btn btn-outline" style={{ width:'100%' }} onClick={() => { setFiltroFolio(''); setFiltroVendedor(''); setFiltroFechaDesde(''); setFiltroFechaHasta('') }}>Limpiar filtros</button>
          </div>
        </div>
        <div style={{ marginTop:'1rem', paddingTop:'1rem', borderTop:'1px solid var(--border, #e2e8f0)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.5rem' }}>
          <span style={{ color:'var(--text-light, #64748b)', fontSize:'0.875rem' }}>
            Mostrando <strong>{ventasFiltro.length}</strong> de {ventas.length} ventas — Total: <strong style={{ color:'var(--secondary, #2563eb)' }}>{formatearMoneda(totalFiltrado)}</strong>
          </span>
          <button className="btn btn-secondary" onClick={manejarExportar} disabled={exportando || ventasFiltro.length === 0} style={{ padding:'0.5rem 1.25rem' }}>
            {exportando ? '⏳ Exportando...' : '📊 Exportar a Excel'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr><th>Folio</th><th>Fecha</th><th>Vendedor</th><th>Método</th><th>Total</th><th>Estado</th><th>Acciones</th></tr>
            </thead>
            <tbody>
              {ventasFiltro.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign:'center', padding:'2rem', color:'var(--text-light, #64748b)' }}>No hay ventas registradas</td></tr>
              ) : ventasFiltro.map(venta => (
                <tr key={venta.id}>
                  <td style={{ fontWeight:'600' }}>#{venta.folio}</td>
                  <td>{formatearFecha(venta.fecha)}</td>
                  <td>{venta.perfiles?.nombre || '-'}</td>
                  <td style={{ fontSize:'0.8rem' }}>{METODO_LABEL[venta.metodo_pago] || '💵 Efectivo'}</td>
                  <td style={{ fontWeight:'600', color: venta.estado==='cancelada' ? 'var(--text-light, #64748b)' : 'var(--secondary, #2563eb)' }}>
                    <span style={{ textDecoration: venta.estado==='cancelada' ? 'line-through' : 'none' }}>{formatearMoneda(venta.total)}</span>
                  </td>
                  <td>
                    {venta.estado === 'cancelada'
                      ? <span className="badge badge-danger">Cancelada</span>
                      : <span className="badge" style={{ background:'#d1fae5', color:'#065f46' }}>Completada</span>}
                  </td>
                  <td>
                    <div style={{ display:'flex', gap:'0.4rem' }}>
                      <button className="btn btn-outline" onClick={() => cargarDetalles(venta.id)} style={{ padding:'0.4rem 0.6rem', fontSize:'0.8rem' }}>Ver</button>
                      <button className="btn btn-outline" onClick={() => abrirTicket(venta)} style={{ padding:'0.4rem 0.6rem', fontSize:'0.8rem' }} title="Reimprimir">🖨️</button>
                      {venta.estado !== 'cancelada' && (
                        <button className="btn btn-outline" onClick={() => cancelarVenta(venta)}
                          disabled={cancelando === venta.id}
                          style={{ padding:'0.4rem 0.6rem', fontSize:'0.8rem', color:'var(--danger, #ef4444)', borderColor:'var(--danger, #ef4444)' }}>
                          {cancelando === venta.id ? '...' : '✕'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {ventaSeleccionada && (
        <div className="modal-overlay" onClick={() => setVentaSeleccionada(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">Detalle de Venta #{ventaDetalle?.folio}</h2></div>
            <div style={{ marginBottom:'1rem', fontSize:'0.875rem', color:'var(--text-light, #64748b)' }}>
              <div>Fecha: {formatearFecha(ventaDetalle?.fecha)}</div>
              <div>Vendedor: {ventaDetalle?.perfiles?.nombre || '-'}</div>
              <div>Método de pago: {METODO_LABEL[ventaDetalle?.metodo_pago] || '💵 Efectivo'}</div>
            </div>
            <div className="table-container">
              <table>
                <thead><tr><th>Producto</th><th>Cantidad</th><th>Precio Unit.</th><th>Subtotal</th></tr></thead>
                <tbody>
                  {detalles.map(d => (
                    <tr key={d.id}>
                      <td><div style={{ fontWeight:'500' }}>{d.productos?.nombre||'Eliminado'}</div>{d.productos?.codigo&&<div style={{ fontSize:'0.875rem', color:'var(--text-light, #64748b)' }}>{d.productos.codigo}</div>}</td>
                      <td>{d.cantidad}</td>
                      <td>{formatearMoneda(d.precio_unitario)}</td>
                      <td style={{ fontWeight:'600' }}>{formatearMoneda(d.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop:'2px solid var(--border, #e2e8f0)' }}>
                    <td colSpan="3" style={{ fontWeight:'700', textAlign:'right', padding:'1rem' }}>Total:</td>
                    <td style={{ fontWeight:'700', fontSize:'1.125rem', color:'var(--primary, #2563eb)' }}>{formatearMoneda(ventaDetalle?.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setVentaSeleccionada(null)}>Cerrar</button>
              <button className="btn btn-primary" onClick={() => { abrirTicket(ventaDetalle); setVentaSeleccionada(null) }}>🖨️ Imprimir</button>
            </div>
          </div>
        </div>
      )}

      {ticketVenta && (
        <TicketImprimible venta={ticketVenta.venta} detalles={ticketVenta.detalles}
          empresa={perfil?.empresa_nombre || perfil?.empresas?.nombre || 'Puntum POS'} vendedor={ticketVenta.venta.perfiles?.nombre||perfil?.nombre||'Vendedor'} onCerrar={() => setTicketVenta(null)} />
      )}
      </>
      )}

      {pestaña === 'compras' && (
        <div className="card">
          {loadingCompras ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-light)' }}>Cargando facturas...</div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr><th>Fecha</th><th>N° Factura</th><th>Proveedor</th><th>Total</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {facturas?.length === 0 ? (
                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>No hay facturas registradas</td></tr>
                  ) : (
                    facturas?.map(f => (
                      <tr key={f.id}>
                        <td>{new Date(f.fecha_emision).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                        <td style={{ fontWeight: '600' }}>{f.numero_factura}</td>
                        <td>{f.razon_social || '-'}</td>
                        <td style={{ fontWeight: '600', color: 'var(--secondary, #2563eb)' }}>{formatearMoneda(f.total)}</td>
                        <td>
                          {f.estado_pago === 'pagada'
                            ? <span className="badge" style={{ background: '#d1fae5', color: '#065f46' }}>Pagada</span>
                            : <span className="badge" style={{ background: '#fef3c7', color: '#92400e' }}>Por Pagar</span>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}